import { Component } from '@theme/component';

const STORAGE_KEY = 'fifi-collector-popup';

/**
 * "Become a collector / Join us" bar that slides up from the bottom of the page after
 * the visitor has scrolled a little or spent some time on the page, over a softly
 * blurred page that keeps scrolling under the wheel. Closing it (close sign, the page
 * behind or Escape) keeps it away for a month in this browser; a successful sign-up
 * keeps it showing the thank-you message.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} sheet - The sliding bar
 * @property {HTMLElement} backdrop - Blurred shade over the page
 *
 * @extends {Component<Refs>}
 */
class FifiPopupComponent extends Component {
  requiredRefs = ['sheet', 'backdrop'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();

    if (this.hasAttribute('data-posted')) {
      this.#remember();
      this.open();
      return;
    }
    if (this.#recentlyClosed() || window.Shopify?.designMode) return;

    const { signal } = this.#controller;
    const delay = Number(this.dataset.delay || 15) * 1000;
    const share = Number(this.dataset.scroll || 40) / 100;
    this.#timer = setTimeout(() => this.open(), delay);

    const check = () => {
      const scroller = document.querySelector('.page-wrapper');
      const element = scroller && scroller.scrollHeight > scroller.clientHeight ? scroller : document.documentElement;
      const reach = (element.scrollTop + element.clientHeight) / element.scrollHeight;
      if (reach >= share) this.open();
    };
    window.addEventListener('scroll', check, { passive: true, signal });
    document.addEventListener('scroll', check, { passive: true, capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  open() {
    if (this.classList.contains('is-open')) return;
    clearTimeout(this.#timer);
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));

    const signal = this.#controller?.signal;
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.close();
      },
      { signal }
    );
    this.addEventListener('wheel', scrollPageWithWheel, { passive: true, signal });
  }

  /** Closes the bar and keeps it away for a month. */
  close() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.classList.remove('is-open');
    this.#remember();
    setTimeout(() => {
      this.hidden = true;
    }, 500);
  }

  #remember() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable: the bar may show again on a later visit
    }
  }

  #recentlyClosed() {
    try {
      const stamp = Number(window.localStorage.getItem(STORAGE_KEY));
      return Boolean(stamp) && Date.now() - stamp < 30 * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }
}

/**
 * Keeps the page scrolling under the bar. On large screens the theme scrolls the
 * page inside .page-wrapper, which a fixed layer would otherwise cut off from the
 * wheel; elsewhere the document scrolls by itself.
 * @param {WheelEvent} event
 */
function scrollPageWithWheel(event) {
  const scroller = document.querySelector('.page-wrapper');
  if (!(scroller instanceof HTMLElement) || scroller.scrollHeight <= scroller.clientHeight) return;

  const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
  scroller.scrollBy({ top: event.deltaY * unit, left: 0, behavior: 'instant' });
}

if (!customElements.get('fifi-popup-component')) {
  customElements.define('fifi-popup-component', FifiPopupComponent);
}
